import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText } from '../../api';
import { upgradeAnbieten } from '../../billtoro/upgrade';
import { Dialog } from '../../components/Dialog';
import { useToast } from '../../lib/toast';
import { ChipRadios } from '../ChipRadios';
import { fehlerText, NUR_INHABER } from '../tarifHinweis';
import type { SeitenProps } from './Steuer';
import { basis, deDatum, type SteuerEinstellung, type SteuerSeite } from './daten';

type Werte = Omit<SteuerEinstellung, 'gespeichert'>;

// Einstellungen zur Umsatzsteuer (/rechnung/steuer/einstellungen): wie das Finanzamt es festgelegt hat (steht im
// Schreiben zur Steuernummer). Ändern darf nur der Inhaber.
export function SteuerEinstellungen({ data, fehltUstva }: SeitenProps) {
  const toast = useToast();
  const [s, setS] = useState<SteuerSeite | null>(null);
  const [w, setW] = useState<Werte | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [frage, setFrage] = useState(false);

  useEffect(() => {
    api
      .get<SteuerSeite>(`${basis(data.firm.id)}/steuer`)
      .then((r) => {
        setS(r);
        setW({ versteuerung: r.versteuerung, zeitraum: r.zeitraum, dauerfrist: r.dauerfrist, bundesland: r.bundesland, euUmsaetze: r.euUmsaetze });
      })
      .catch((err) => setError(errorText(err)));
  }, [data.firm.id]);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    if (!w) return;
    if (fehltUstva) {
      upgradeAnbieten(data.tarif, 'ustva');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.put(`${basis(data.firm.id)}/steuer`, w);
      setS(await api.get<SteuerSeite>(`${basis(data.firm.id)}/steuer`));
      toast('Gespeichert');
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  async function widerrufen() {
    setBusy(true);
    try {
      const r = await api.del<{ auftrag: SteuerSeite['auftrag'] }>(`${basis(data.firm.id)}/elster-auftrag`);
      setS((v) => (v ? { ...v, auftrag: r.auftrag } : v));
      setFrage(false);
      toast('Widerrufen');
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!s || !w) return error ? <div className="notice bad">{error}</div> : <div className="spinner" aria-label="Lädt" />;
  const set = (p: Partial<Werte>) => setW({ ...w, ...p });
  const land = s.bundeslaender.find((b) => b.id === w.bundesland);

  return (
    <>
      <div className="rt-head">
        <div>
          <h1>Einstellungen zur Umsatzsteuer</h1>
          <p className="muted">So, wie es im Schreiben vom Finanzamt zu Ihrer Steuernummer steht.</p>
        </div>
      </div>
      {s.kleinunternehmer && (
        <div className="notice info">
          Sie sind als Kleinunternehmer eingetragen (in <Link to="/rechnung/angaben">Meine Angaben</Link>). Dann geben Sie keine Voranmeldung ab.
        </div>
      )}
      <form className="rt-card ag-einst" onSubmit={(e) => void speichern(e)} noValidate>
        <fieldset disabled={!s.inhaber}>
          {!s.inhaber && <p className="rt-hint">{NUR_INHABER}</p>}
          <div className="field">
            <span>Wie oft geben Sie ab?</span>
            <ChipRadios<'monat' | 'quartal'>
              label="Wie oft"
              value={w.zeitraum}
              options={[
                ['monat', 'Jeden Monat'],
                ['quartal', 'Jedes Quartal'],
              ]}
              onChange={(v) => set({ zeitraum: v })}
            />
          </div>
          <label className="ag-check">
            <input type="checkbox" checked={w.dauerfrist} onChange={(e) => set({ dauerfrist: e.target.checked })} />
            <span>Ich habe einen Monat mehr Zeit (Dauerfristverlängerung)</span>
          </label>
          <div className="field">
            <span>Wann zählt eine Rechnung?</span>
            <ChipRadios<'soll' | 'ist'>
              label="Wann zählt eine Rechnung"
              value={w.versteuerung}
              options={[
                ['soll', 'Mit dem Rechnungsdatum'],
                ['ist', 'Wenn das Geld da ist'],
              ]}
              onChange={(v) => set({ versteuerung: v })}
            />
            <small>„Wenn das Geld da ist“ nur, wenn das Finanzamt es Ihnen erlaubt hat. Im Zweifel: mit dem Rechnungsdatum.</small>
          </div>
          <label className="field">
            <span>Bundesland Ihres Finanzamts</span>
            <select value={w.bundesland ?? ''} onChange={(e) => set({ bundesland: e.target.value || null })}>
              <option value="">Bitte wählen</option>
              {s.bundeslaender.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            {land && <small>Steuernummer sieht dort so aus: {land.muster}</small>}
          </label>
          {s.steuernummer ? (
            s.steuernummerPasst === false ? (
              <div className="notice warn">
                Ihre Steuernummer {s.steuernummer} passt nicht zum Bundesland. Bitte in <Link to="/rechnung/angaben">Meine Angaben</Link> prüfen.
              </div>
            ) : (
              s.steuernummerPasst && <p className="muted ag-klein">Steuernummer {s.steuernummer} passt.</p>
            )
          ) : (
            <div className="notice warn">
              Ihre Steuernummer fehlt noch in <Link to="/rechnung/angaben">Meine Angaben</Link>.
            </div>
          )}
          <div className="field">
            <span>Verkäufe an Firmen in anderen EU-Ländern sind meist</span>
            <ChipRadios<'leistungen' | 'waren'>
              label="EU-Verkäufe"
              value={w.euUmsaetze}
              options={[
                ['leistungen', 'Dienstleistungen'],
                ['waren', 'Waren'],
              ]}
              onChange={(v) => set({ euUmsaetze: v })}
            />
            <small>Nur wichtig, wenn Sie an Firmen im EU-Ausland verkaufen.</small>
          </div>
          {error && <div className="notice bad">{error}</div>}
          <div className="dlg-actions">
            <button type="submit" className="btn primary" disabled={busy}>
              Speichern
            </button>
          </div>
        </fieldset>
      </form>
      {(s.auftrag.erteilt || s.auftrag.moeglich) && (
        <div className="rt-card ag-einst">
          <h2 className="ag-h2">Billtoro sendet für Sie</h2>
          {s.auftrag.erteilt ? (
            <div className="ag-auftrag">
              <p className="muted">
                Zugestimmt am {deDatum(s.auftrag.erteilt.am.slice(0, 10))} von {s.auftrag.erteilt.name}. Billtoro sendet Ihre Voranmeldungen, wenn Sie
                es auslösen.
              </p>
              {s.inhaber && (
                <button type="button" className="btn" disabled={busy} onClick={() => setFrage(true)}>
                  Widerrufen
                </button>
              )}
            </div>
          ) : (
            <p className="muted">Beim ersten Senden stimmen Sie einmal zu. Ein eigenes ELSTER-Zertifikat brauchen Sie dann nicht.</p>
          )}
        </div>
      )}
      <Dialog open={frage} onClose={() => setFrage(false)} title="Auftrag widerrufen?" className="ag-dlg-klein">
        <p>Billtoro sendet dann nicht mehr für Sie. Mit eigenem Zertifikat oder selbst in „Mein ELSTER“ geht es weiter.</p>
        <div className="dlg-actions">
          <button type="button" className="btn" onClick={() => setFrage(false)}>
            Abbrechen
          </button>
          <button type="button" className="btn primary rt-danger" disabled={busy} onClick={() => void widerrufen()}>
            Widerrufen
          </button>
        </div>
      </Dialog>
    </>
  );
}
